import { useToolSiteId } from "#app/sites/site-tool-context";
import { useAtomValue } from "@effect/atom-react";
import { useAuth } from "@crm/access/hooks/use-auth";
import { queryAtomEffect } from "@crm/shared/query-atom/effect";
import { Button } from "@crm/ui/components/ui/button";
import { useIsSioStudent } from "@crm/ui/hooks/use-is-sio-student";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { ArrowRight, CheckCircle2, FileText, Info, Lightbulb, Link2, PenLine } from "lucide-react";
import { useMemo, useState } from "react";
import { WorkbenchDrawer } from "#components/workbench-drawer";
import { useStudentSioSessions } from "#app/sio/student-sio.queries";
import { BlogWorkbench } from "../blog/index";
import { InsightInterviewForTopic, loadInsightTopicEffect } from "../insight/index";
import { AdminSioSetup } from "../topics/index";

export const Route = createFileRoute("/_protected/sio-tools/_layout/pipeline/")({
  head: () => ({}),
  component: PipelinePage,
});

type PipelineTopic = {
  id: string;
  studentId: string;
  topic: string;
  insight: string | null;
  category: "B" | "C" | "BC" | "unknown" | null;
  articleWrittenAt: Date | null;
  pipelineEnteredAt: Date | null;
  tagName: string | null;
};

type LaneId = "co" | "write" | "done";

const LANES: ReadonlyArray<{
  id: LaneId;
  name: string;
  hint: string;
  dotClass: string;
}> = [
  { id: "co", name: "共创中", hint: "磨 Brief", dotClass: "bg-violet" },
  { id: "write", name: "待写作", hint: "Brief 就绪", dotClass: "bg-brand" },
  { id: "done", name: "已完成", hint: "发布 + 润色", dotClass: "bg-success" },
];

/** 阶段推导（与选题库同源）：共创中=推入无 insight；待写作=有 insight 未写；已完成=已写 */
function laneOf(topic: PipelineTopic): LaneId | null {
  if (topic.articleWrittenAt) {
    return "done";
  }
  if (topic.insight) {
    return "write";
  }
  if (topic.pipelineEnteredAt) {
    return "co";
  }
  return null;
}

/**
 * Insight 工位的话题种子查询（effect-atom 版）。
 *
 * 按需加载完整话题行；订阅 `"contentTopic"` 域键，
 * 携带该键的 mutation 成功后自动刷新。staleTime 兜底跨页重验。
 */
const insightTopicAtom = queryAtomEffect(
  (key: readonly [string, string | undefined, string]) =>
    loadInsightTopicEffect({ topicId: key[0], studentId: key[1], siteId: key[2] }),
  { staleTime: 30_000, reactivityKeys: ["contentTopic"] },
);

function categoryBadge(category: PipelineTopic["category"]) {
  if (!category) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 font-semibold text-[10.5px] text-ink-3">
        待分类
      </span>
    );
  }
  const map = {
    B: { label: "B端", cls: "bg-info-soft text-info" },
    C: { label: "C端", cls: "bg-success-soft text-success" },
    BC: { label: "BC", cls: "bg-violet-soft text-violet" },
    unknown: { label: "未知", cls: "bg-muted text-ink-3" },
  } as const;
  const item = map[category] ?? map.unknown;
  return (
    <span className={`rounded-full px-2 py-0.5 font-bold text-[10.5px] ${item.cls}`}>
      {item.label}
    </span>
  );
}

function AssetCard({
  topic,
  onOpenWorkbench,
}: {
  topic: PipelineTopic;
  onOpenWorkbench: (type: "insight" | "blog", topic: PipelineTopic) => void;
}) {
  const lane = laneOf(topic);
  return (
    <div className="hover:-translate-y-px rounded-xl border border-border bg-card p-3.5 shadow-s transition-all hover:border-border-strong hover:shadow-m">
      <div className="mb-2 font-semibold text-[13px] text-ink leading-snug">{topic.topic}</div>
      <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
        {topic.tagName && (
          <span className="rounded-full border border-border bg-background px-2 py-0.5 font-medium text-[10.5px] text-ink-2">
            {topic.tagName}
          </span>
        )}
        {categoryBadge(topic.category)}
      </div>
      {lane === "co" && (
        <Button className="w-full" onClick={() => onOpenWorkbench("insight", topic)} size="sm">
          <Lightbulb className="size-3.5" />
          继续共创
        </Button>
      )}
      {lane === "write" && (
        <>
          <div className="mb-2 flex items-center gap-1 font-semibold text-[11px] text-violet">
            <CheckCircle2 size={12} />
            Brief 就绪
          </div>
          <Button className="w-full" onClick={() => onOpenWorkbench("blog", topic)} size="sm">
            <PenLine className="size-3.5" />
            开始写作
          </Button>
        </>
      )}
      {lane === "done" && (
        <div className="flex gap-1.5">
          <Button asChild className="flex-1" size="sm" variant="outline">
            <Link to="/sio-tools/external-links">
              <Link2 className="size-3.5" />
              润色引用
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}

function PipelinePage() {
  const siteId = useToolSiteId();
  const { user } = useAuth();
  const isSioStudent = useIsSioStudent();
  const canUseSioTopics = !!user && isSioStudent;
  const [overrideStudentId, setOverrideStudentId] = useState<string | null>(null);
  const effectiveStudentId = user?.studentId ?? overrideStudentId;

  // 工位抽屉状态：打开的工位类型 + 对应内容资产
  const [workbench, setWorkbench] = useState<{
    type: "insight" | "blog";
    topic: PipelineTopic;
  } | null>(null);

  // Insight 工位需要完整话题行（insight 种子），按需加载
  const insightTopicResult = useAtomValue(
    insightTopicAtom(
      [
        workbench?.type === "insight" ? workbench.topic.id : "",
        workbench?.type === "insight" ? workbench.topic.studentId : undefined,
        siteId,
      ],
      workbench?.type === "insight",
    ),
  );
  const insightTopic = AsyncResult.value(insightTopicResult).pipe(Option.getOrUndefined) ?? null;

  const sessionsResult = useStudentSioSessions(effectiveStudentId, canUseSioTopics);
  const sessions = AsyncResult.value(sessionsResult).pipe(Option.getOrElse(() => []));
  const isLoading = AsyncResult.isWaiting(sessionsResult);

  const laneTopics = useMemo(() => {
    const flat: PipelineTopic[] = sessions.flatMap((session) =>
      session.topics.map((topic) => ({
        id: topic.id,
        studentId: topic.studentId,
        topic: topic.topic,
        insight: topic.insight,
        category: topic.category,
        articleWrittenAt: topic.articleWrittenAt,
        pipelineEnteredAt: topic.pipelineEnteredAt,
        tagName: topic.tagName,
      })),
    );
    const map: Record<LaneId, PipelineTopic[]> = {
      co: [],
      write: [],
      done: [],
    };
    for (const topic of flat) {
      const lane = laneOf(topic);
      if (lane) {
        map[lane].push(topic);
      }
    }
    return map;
  }, [sessions]);

  if (!canUseSioTopics) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <h1 className="mb-2 font-bold text-2xl text-foreground">内容流水线</h1>
        <div className="flex flex-col items-center justify-center rounded-lg border border-warning bg-warning-soft p-12 text-center">
          <FileText className="mx-auto mb-4 h-16 w-16 text-warning" />
          <h2 className="mb-2 font-bold text-2xl text-warning">需要 SIO 学员身份</h2>
          <p className="text-warning">此功能仅对参加 SIO 课程的学员开放。</p>
        </div>
      </div>
    );
  }

  if (!effectiveStudentId) {
    return <AdminSioSetup onReady={setOverrideStudentId} />;
  }

  const totalInPipeline = laneTopics.co.length + laneTopics.write.length + laneTopics.done.length;

  return (
    <div className="mx-auto max-w-7xl p-8">
      {/* Hero */}
      <div className="mb-2 flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="mb-1.5 font-display font-semibold text-[28px] text-ink tracking-tight">
            内容<span className="font-display text-brand italic">流水线</span>
          </h1>
          <p className="max-w-xl text-[13.5px] text-ink-2 leading-relaxed">
            手头在做的内容都在这里。推入 → 共创出 Brief → 写作 → 完成发布。
          </p>
        </div>
      </div>
      <div className="mb-5 flex items-center gap-1.5 text-[12px] text-ink-3">
        <Info size={13} />
        新选题从
        <Link className="font-semibold text-brand-text hover:underline" to="/sio-tools/topics">
          选题库
        </Link>
        推入 · 看板只显示在进行中的内容（{totalInPipeline}）
      </div>

      {isLoading ? (
        <div className="py-10 text-[13px] text-ink-3">加载中…</div>
      ) : totalInPipeline === 0 ? (
        <div className="rounded-xl border border-border border-dashed px-6 py-12 text-center">
          <p className="mb-4 text-[13px] text-ink-3">流水线还是空的，去选题库推入第一条话题</p>
          <Button asChild size="sm" variant="outline">
            <Link to="/sio-tools/topics">
              去选题库
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      ) : (
        <div className="grid items-start gap-3.5 md:grid-cols-3">
          {LANES.map((lane) => (
            <div className="rounded-2xl border border-border bg-muted/60 p-3" key={lane.id}>
              <div className="flex items-center gap-2 px-1.5 pt-0.5 pb-3">
                <span className={`h-2 w-2 rounded-full ${lane.dotClass}`} />
                <h3 className="font-bold text-[13.5px] text-ink tracking-tight">{lane.name}</h3>
                <span className="rounded-full border border-border bg-card px-2 py-0.5 font-bold text-[11px] text-ink-2">
                  {laneTopics[lane.id].length}
                </span>
                <span className="ml-auto text-[10.5px] text-ink-3">{lane.hint}</span>
              </div>
              <div className="flex flex-col gap-2.5">
                {laneTopics[lane.id].length === 0 ? (
                  <div className="rounded-xl border border-border border-dashed px-4 py-6 text-center text-[12px] text-ink-3">
                    暂无
                  </div>
                ) : (
                  laneTopics[lane.id].map((topic) => (
                    <AssetCard
                      key={topic.id}
                      onOpenWorkbench={(type, t) => setWorkbench({ type, topic: t })}
                      topic={topic}
                    />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 工位抽屉：共创 / 写作 */}
      {workbench?.type === "insight" && (
        <WorkbenchDrawer
          onClose={() => setWorkbench(null)}
          subtitle={
            <>
              <span className="inline-flex items-center rounded-full bg-violet-soft px-2.5 py-0.5 font-semibold text-[11px] text-violet">
                共创中
              </span>
              {workbench.topic.tagName && (
                <span className="rounded-full border border-border bg-background px-2 py-0.5 font-medium text-[10.5px] text-ink-2">
                  {workbench.topic.tagName}
                </span>
              )}
              {categoryBadge(workbench.topic.category)}
            </>
          }
          title={workbench.topic.topic}
        >
          <InsightInterviewForTopic
            key={workbench.topic.id}
            search={{
              topicId: workbench.topic.id,
              studentId: workbench.topic.studentId,
              title: workbench.topic.topic,
            }}
            topic={insightTopic}
            variant="drawer"
          />
        </WorkbenchDrawer>
      )}
      {workbench?.type === "blog" && (
        <WorkbenchDrawer
          onClose={() => setWorkbench(null)}
          subtitle={
            <>
              <span className="inline-flex items-center rounded-full bg-brand-soft px-2.5 py-0.5 font-semibold text-[11px] text-brand-text">
                写作中
              </span>
              {workbench.topic.tagName && (
                <span className="rounded-full border border-border bg-background px-2 py-0.5 font-medium text-[10.5px] text-ink-2">
                  {workbench.topic.tagName}
                </span>
              )}
              {categoryBadge(workbench.topic.category)}
            </>
          }
          title={workbench.topic.topic}
          wide
        >
          <BlogWorkbench
            search={{
              title: workbench.topic.topic,
              insight: workbench.topic.insight ?? "",
            }}
            variant="drawer"
          />
        </WorkbenchDrawer>
      )}
    </div>
  );
}
